"use client"

import { useState, useEffect, useMemo } from "react"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from "@/utils/supabase/client"
import { useAuth } from "@/contexts/auth-context"
import { Payment, Patient } from "@/types"
import { Loader2 } from "lucide-react"
import { format, differenceInDays } from "date-fns"

type OutstandingPayment = Payment & {
  patients: Pick<Patient, 'name'> | null
}

interface OutstandingStats {
    total: number;
    buckets: {
        '0-30': number;
        '31-60': number;
        '61-90': number;
        '90+': number;
    };
}

export function OutstandingPayments() {
  const [payments, setPayments] = useState<OutstandingPayment[]>([])
  const [stats, setStats] = useState<OutstandingStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const { currentOrganization } = useAuth()

  useEffect(() => {
    const fetchOutstanding = async () => {
      if (!currentOrganization) return;
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("payments")
          .select(`*, patients(name)`)
          .eq("organization_id", currentOrganization.id)
          .in('status', ['pending', 'partial']);

        if (error) throw error;
        
        const mappedData = data.map(p => ({ ...p, amount: p.amount_due, payment_date: p.created_at }))

        let total = 0;
        const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
        
        mappedData.forEach(p => {
            const balance = p.amount - (p.amount_paid || 0);
            total += balance;
            const age = differenceInDays(new Date(), new Date(p.payment_date));

            if (age <= 30) buckets['0-30'] += balance;
            else if (age <= 60) buckets['31-60'] += balance;
            else if (age <= 90) buckets['61-90'] += balance;
            else buckets['90+'] += balance;
        });

        setPayments(mappedData as OutstandingPayment[]);
        setStats({ total, buckets });

      } catch (error: any) {
        toast({
          title: "Error fetching payments",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchOutstanding();
  }, [currentOrganization, toast]);

  const columns: ColumnDef<OutstandingPayment>[] = useMemo(() => [
    { accessorKey: "id", header: "Reference" },
    { 
      accessorKey: "created_at", 
      header: "Date",
      cell: ({ row }) => format(new Date(row.getValue("created_at")), "PPP")
    },
    { accessorKey: "patients.name", header: "Patient", cell: ({ row }) => row.original.patients?.name || 'N/A'},
    { 
      id: "age",
      header: "Age (Days)",
      cell: ({ row }) => differenceInDays(new Date(), new Date(row.original.created_at))
    },
    { 
      accessorKey: "amount_due", 
      header: "Amount Due",
      cell: ({ row }) => `Rs. ${row.getValue("amount_due")?.toLocaleString()}`
    },
    { 
      accessorKey: "amount_paid", 
      header: "Amount Paid",
      cell: ({ row }) => `Rs. ${(row.getValue("amount_paid") || 0).toLocaleString()}`
    },
    {
      id: "balance",
      header: "Balance",
      cell: ({ row }) => {
        const balance = row.original.amount_due - (row.original.amount_paid || 0);
        return `Rs. ${balance.toLocaleString()}`
      }
    },
    { 
      accessorKey: "status", 
      header: "Status",
      cell: ({ row }) => <Badge variant="outline" className="capitalize">{row.getValue("status")}</Badge>
    },
  ], []);

  const table = useReactTable({
    data: payments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
     <div className="space-y-6 mt-6">
         <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
             <Card className="lg:col-span-1 bg-destructive/10 border-destructive">
                 <CardHeader><CardTitle>Total Outstanding</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-3xl font-bold">Rs. {(stats?.total || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
             <Card>
                 <CardHeader><CardTitle>0-30 Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(stats?.buckets['0-30'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
             <Card>
                 <CardHeader><CardTitle>31-60 Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(stats?.buckets['31-60'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
              <Card>
                 <CardHeader><CardTitle>61-90 Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(stats?.buckets['61-90'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
              <Card>
                 <CardHeader><CardTitle>90+ Days</CardTitle></CardHeader>
                 <CardContent>
                     <div className="text-2xl font-bold">Rs. {(stats?.buckets['90+'] || 0).toLocaleString()}</div>
                 </CardContent>
             </Card>
         </div>
 
         <Card>
             <CardHeader><CardTitle>Outstanding Payments Details</CardTitle></CardHeader>
             <CardContent>
                 <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                                ))}
                                </TableRow>
                            ))
                            ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                No outstanding payments. Great job!
                                </TableCell>
                            </TableRow>
                            )}
                        </TableBody>
                    </Table>
                 </div>
             </CardContent>
         </Card>
     </div>
  )
} 